import { useState, useMemo } from 'react'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuGroup, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from '@duck/ui'
import { Plus, Columns2 } from 'lucide-react'
import { workspaceTabsDefinitions, workspaceTabsOrder } from './workspaceTabs.definitions'
import { ChallengeService } from '@duck/api-definitions'
import { getTabClassName } from './WorkspaceTab'
import { useTabStyle } from './tabStyle'
import { useTar } from '@/modules/i18n'
import type { TranslationKey } from '@/modules/i18n'
import { useAtomValue } from 'jotai'
import { workspaceRecentTipsAtom, workspaceChallengeIdAtom } from '@/store/workspaceAtoms'
import { trackWorkspace } from '@/modules/workspaceAnalytics'
import { useAvailableWorkspaceTabs } from '@/hooks/useAvailableWorkspaceTabs'

export interface AddWorkspaceTabTabProps {
    onAddTab: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
    onOpenInSplitView?: (tabType: ChallengeService.WorkspaceTabTypeEnum) => void
    /** Tooltip for the split/other-view button: e.g. 'common.openInSplitView' or 'common.openInOtherView' */
    openInSplitViewTooltipKey?: TranslationKey
    /** Set of tab types currently open across all views */
    openTabTypes?: Set<ChallengeService.WorkspaceTabTypeEnum>
    className?: string
}

export const AddWorkspaceTabTab = ({ onAddTab, onOpenInSplitView, openInSplitViewTooltipKey = 'common.openInSplitView', openTabTypes, className }: AddWorkspaceTabTabProps) => {
    const { t, isRtl } = useTar()
    const [isOpen, setIsOpen] = useState(false)
    const recentTips = useAtomValue(workspaceRecentTipsAtom)
    const challengeId = useAtomValue(workspaceChallengeIdAtom)
    const availableTabs = useAvailableWorkspaceTabs()
    const tabStyle = useTabStyle()
    const visibleTabsOrder = useMemo(
        () => workspaceTabsOrder.filter((type) => {
            if (!availableTabs.has(type)) return false
            if (type === ChallengeService.WorkspaceTabTypeEnum.TipsHistory) return recentTips.length > 0
            return true
        }),
        [recentTips, availableTabs]
    )

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <div
                    role="button"
                    tabIndex={0}
                    className={getTabClassName(false, false, true, className, tabStyle)}
                    style={{ zIndex: 1 }}
                >
                    <span className="relative z-10 pt-1">
                        <Plus className="size-4" />
                    </span>
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-48">
                <DropdownMenuGroup>
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">{t('common.newTab')}</DropdownMenuLabel>
                    {visibleTabsOrder.map((tabType) => {
                        const def = workspaceTabsDefinitions[tabType]
                        const DefIcon = def.Icon
                        const isOnlyOneAndOpen = !!def.onlyOne && !!openTabTypes?.has(tabType)

                        if (isOnlyOneAndOpen) {
                            return (
                                <TooltipProvider key={tabType}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div className="w-full">
                                                <DropdownMenuItem
                                                    disabled
                                                    className="flex-1 cursor-not-allowed opacity-50 gap-2"
                                                >
                                                    <DefIcon className={cn('size-4 shrink-0 me-1', def.iconColor)} aria-hidden />
                                                    {t(def.label)}
                                                </DropdownMenuItem>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {t('common.onlyOneTabOpen', { tabName: t(def.label) })}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )
                        }

                        return (
                            <div key={tabType} className="group flex items-center">
                                <DropdownMenuItem
                                    onClick={() => {
                                        onAddTab(tabType)
                                        trackWorkspace('workspace:tab:add', { challengeId, tabType })
                                    }}
                                    className="flex-1 cursor-pointer gap-2 hover:bg-transparent focus:bg-transparent hover:text-primary focus:text-primary"
                                >
                                    <DefIcon className={cn('size-4 shrink-0 me-1', def.iconColor)} aria-hidden />
                                    {t(def.label)}
                                </DropdownMenuItem>
                                {onOpenInSplitView && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    className="p-2 opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity hover:text-primary focus:text-primary cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        onOpenInSplitView(tabType)
                                                        trackWorkspace('workspace:split:open', { challengeId, tabType })
                                                        setIsOpen(false)
                                                    }}
                                                >
                                                    <Columns2 className="size-4" />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent side={isRtl ? 'left' : 'right'}>
                                                {t(openInSplitViewTooltipKey)}
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                        )
                    })}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
